import { useEffect, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, RefreshCw, Play } from 'lucide-react';

import {
  llmPlatformAPI,
  type LlmChannel,
  type LlmModel,
} from '../../../baselines/llm/llmPlatformAPI';
import toast from '../../Toast';

interface FormState {
  id?: string;
  name: string;
  channelId: string;
  modelIdString: string;
  isActive: boolean;
  supportsStream: boolean;
  supportsJsonMode: boolean;
  supportsTools: boolean;
  supportsImages: boolean;
  hasReasoning: boolean;
  contextLength: number;
  maxOutputTokens: number;
  promptPriceCredits: number | '';
  completionPriceCredits: number | '';
  remark: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  channelId: '',
  modelIdString: '',
  isActive: true,
  supportsStream: true,
  supportsJsonMode: false,
  supportsTools: false,
  supportsImages: false,
  hasReasoning: false,
  contextLength: 8192,
  maxOutputTokens: 2048,
  promptPriceCredits: '',
  completionPriceCredits: '',
  remark: '',
};

export default function LlmModelsTab() {
  const [models, setModels] = useState<LlmModel[]>([]);
  const [channels, setChannels] = useState<LlmChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<FormState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState<{ modelId: string; modelName: string } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, c] = await Promise.all([
        llmPlatformAPI.listModels(),
        llmPlatformAPI.listChannels(),
      ]);
      setModels(m.data);
      setChannels(c.data);
    } catch (err) {
      toast.error(`加载失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const onCreate = () => {
    if (channels.length === 0) {
      toast.error('请先创建至少一个 LLM 渠道');
      return;
    }
    setEditing({ ...EMPTY_FORM, channelId: channels[0].id });
  };

  const onEdit = (m: LlmModel) => {
    setEditing({
      id: m.id,
      name: m.name,
      channelId: m.channelId,
      modelIdString: m.modelIdString,
      isActive: m.isActive,
      supportsStream: m.supportsStream,
      supportsJsonMode: m.supportsJsonMode,
      supportsTools: m.supportsTools,
      supportsImages: m.supportsImages,
      hasReasoning: m.hasReasoning,
      contextLength: m.contextLength,
      maxOutputTokens: m.maxOutputTokens,
      promptPriceCredits: m.promptPriceCredits ?? '',
      completionPriceCredits: m.completionPriceCredits ?? '',
      remark: m.remark ?? '',
    });
  };

  const onDelete = async (m: LlmModel) => {
    if (!confirm(`确定删除模型 ${m.name}？`)) return;
    try {
      await llmPlatformAPI.deleteModel(m.id);
      toast.success('已删除');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '删除失败');
    }
  };

  const onSubmit = async () => {
    if (!editing) return;
    if (!editing.name.trim() || !editing.modelIdString.trim() || !editing.channelId) {
      toast.error('请填写名称、modelIdString、渠道');
      return;
    }
    setSubmitting(true);
    try {
      const payload: Partial<LlmModel> = {
        name: editing.name.trim(),
        channelId: editing.channelId,
        modelIdString: editing.modelIdString.trim(),
        isActive: editing.isActive,
        supportsStream: editing.supportsStream,
        supportsJsonMode: editing.supportsJsonMode,
        supportsTools: editing.supportsTools,
        supportsImages: editing.supportsImages,
        hasReasoning: editing.hasReasoning,
        contextLength: editing.contextLength,
        maxOutputTokens: editing.maxOutputTokens,
        promptPriceCredits: editing.promptPriceCredits === '' ? null : Number(editing.promptPriceCredits),
        completionPriceCredits: editing.completionPriceCredits === '' ? null : Number(editing.completionPriceCredits),
        remark: editing.remark.trim() || null,
      };
      if (editing.id) {
        await llmPlatformAPI.updateModel(editing.id, payload);
        toast.success('已更新');
      } else {
        await llmPlatformAPI.createModel(payload);
        toast.success('已创建');
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-semibold text-light-text">LLM 模型</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-light-border hover:bg-light-hover"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            刷新
          </button>
          <button
            onClick={onCreate}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-accent-blue text-white hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            新增模型
          </button>
        </div>
      </div>

      {loading && models.length === 0 ? (
        <div className="flex items-center justify-center py-10 text-light-muted">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> 加载中
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-light-border overflow-hidden">
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-light-hover text-light-muted">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">名称</th>
                  <th className="px-4 py-3 text-left font-medium">modelId</th>
                  <th className="px-4 py-3 text-left font-medium">渠道</th>
                  <th className="px-4 py-3 text-left font-medium">能力</th>
                  <th className="px-4 py-3 text-left font-medium">定价 (1k tokens)</th>
                  <th className="px-4 py-3 text-left font-medium">状态</th>
                  <th className="px-4 py-3 text-right font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {models.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-light-muted">
                      暂无模型
                    </td>
                  </tr>
                )}
                {models.map((m) => (
                  <tr key={m.id} className="border-t border-light-border">
                    <td className="px-4 py-3 font-medium">{m.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{m.modelIdString}</td>
                    <td className="px-4 py-3 text-xs">
                      {getChannelLabel(m)}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <CapabilityBadges m={m} />
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {getPricingLabel(m)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill isActive={m.isActive} />
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <ModelActions
                        model={m}
                        onTest={(model) => setTesting({ modelId: model.id, modelName: model.name })}
                        onEdit={onEdit}
                        onDelete={onDelete}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="divide-y divide-light-border lg:hidden">
            {models.length === 0 ? (
              <div className="px-4 py-8 text-center text-light-muted">暂无模型</div>
            ) : (
              models.map((m) => (
                <div key={m.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-light-text">{m.name}</div>
                      <div className="mt-1 break-all font-mono text-xs text-light-muted">{m.modelIdString}</div>
                    </div>
                    <StatusPill isActive={m.isActive} />
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <InfoBlock label="渠道" value={getChannelLabel(m)} />
                    <InfoBlock label="定价" value={getPricingLabel(m)} />
                  </div>
                  <div>
                    <div className="mb-1 text-xs text-light-muted">能力</div>
                    <CapabilityBadges m={m} />
                  </div>
                  <ModelActions
                    model={m}
                    onTest={(model) => setTesting({ modelId: model.id, modelName: model.name })}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    mobile
                  />
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {editing && (
        <ModelEditModal
          editing={editing}
          channels={channels}
          submitting={submitting}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSubmit={() => void onSubmit()}
        />
      )}

      {testing && (
        <TestCallModal
          modelId={testing.modelId}
          modelName={testing.modelName}
          onClose={() => setTesting(null)}
        />
      )}
    </div>
  );
}

function CapabilityBadges({ m }: { m: LlmModel }) {
  const items: string[] = [];
  if (m.supportsStream) items.push('流式');
  if (m.supportsJsonMode) items.push('JSON');
  if (m.supportsTools) items.push('Tools');
  if (m.supportsImages) items.push('图像');
  if (m.hasReasoning) items.push('推理');
  if (items.length === 0) return <span className="text-light-muted">-</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((t) => (
        <span key={t} className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{t}</span>
      ))}
    </div>
  );
}

function getChannelLabel(m: LlmModel): string {
  if (!m.channel) return '-';
  return `${m.channel.name}${!m.channel.isActive ? '（渠道禁用）' : ''}`;
}

function getPricingLabel(m: LlmModel): string {
  return m.promptPriceCredits != null && m.completionPriceCredits != null
    ? `in ${m.promptPriceCredits} / out ${m.completionPriceCredits}`
    : '未设置';
}

function StatusPill({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">启用</span>
  ) : (
    <span className="px-2 py-1 text-xs rounded bg-gray-200 text-gray-600">禁用</span>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-light-hover p-3">
      <div className="text-xs text-light-muted">{label}</div>
      <div className="mt-1 break-all text-sm text-light-text">{value}</div>
    </div>
  );
}

function ModelActions({
  model,
  onTest,
  onEdit,
  onDelete,
  mobile = false,
}: {
  model: LlmModel;
  onTest: (model: LlmModel) => void;
  onEdit: (model: LlmModel) => void;
  onDelete: (model: LlmModel) => Promise<void>;
  mobile?: boolean;
}) {
  const baseClass = mobile
    ? 'inline-flex items-center gap-1 px-3 py-2 text-xs rounded border border-light-border hover:bg-light-hover'
    : 'inline-flex items-center gap-1 px-2 py-1 text-xs rounded';
  return (
    <div className={mobile ? 'flex flex-wrap gap-2' : 'whitespace-nowrap'}>
      <button
        onClick={() => onTest(model)}
        className={`${baseClass} ${mobile ? 'text-blue-600' : 'hover:bg-blue-50 text-blue-600'}`}
      >
        <Play className="w-3.5 h-3.5" /> 测试
      </button>
      <button
        onClick={() => onEdit(model)}
        className={`${baseClass} ${mobile ? '' : 'hover:bg-light-hover ml-1'}`}
      >
        <Pencil className="w-3.5 h-3.5" /> 编辑
      </button>
      <button
        onClick={() => void onDelete(model)}
        className={
          mobile
            ? 'inline-flex items-center gap-1 px-3 py-2 text-xs rounded border border-rose-200 hover:bg-red-50 text-red-600'
            : 'inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-red-50 text-red-600 ml-1'
        }
      >
        <Trash2 className="w-3.5 h-3.5" /> 删除
      </button>
    </div>
  );
}

interface ModalProps {
  editing: FormState;
  channels: LlmChannel[];
  submitting: boolean;
  onChange: (f: FormState) => void;
  onCancel: () => void;
  onSubmit: () => void;
}

function ModelEditModal({ editing, channels, submitting, onChange, onCancel, onSubmit }: ModalProps) {
  const isEdit = !!editing.id;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-light-border flex items-center justify-between">
          <h3 className="text-lg font-semibold">{isEdit ? '编辑模型' : '新增模型'}</h3>
          <button onClick={onCancel} className="text-light-muted hover:text-light-text">×</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="模型名" required>
              <input className="input" value={editing.name} onChange={(e) => onChange({ ...editing, name: e.target.value })} />
            </Field>
            <Field label="渠道" required>
              <select
                className="input"
                value={editing.channelId}
                onChange={(e) => onChange({ ...editing, channelId: e.target.value })}
              >
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{!c.isActive ? '（禁用）' : ''}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="modelIdString（API body.model 入参）" required>
            <input
              className="input font-mono"
              placeholder="deepseek-chat / gpt-4o-mini / qwen-plus"
              value={editing.modelIdString}
              onChange={(e) => onChange({ ...editing, modelIdString: e.target.value })}
            />
          </Field>

          <div>
            <label className="block text-sm text-light-muted mb-2">能力</label>
            <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
              <Check label="流式" checked={editing.supportsStream} onChange={(v) => onChange({ ...editing, supportsStream: v })} />
              <Check label="JSON 模式" checked={editing.supportsJsonMode} onChange={(v) => onChange({ ...editing, supportsJsonMode: v })} />
              <Check label="Tools" checked={editing.supportsTools} onChange={(v) => onChange({ ...editing, supportsTools: v })} />
              <Check label="图像输入" checked={editing.supportsImages} onChange={(v) => onChange({ ...editing, supportsImages: v })} />
              <Check label="推理（reasoning）" checked={editing.hasReasoning} onChange={(v) => onChange({ ...editing, hasReasoning: v })} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="contextLength（tokens）">
              <input
                type="number"
                className="input"
                value={editing.contextLength}
                onChange={(e) => onChange({ ...editing, contextLength: Number(e.target.value) || 0 })}
              />
            </Field>
            <Field label="maxOutputTokens">
              <input
                type="number"
                className="input"
                value={editing.maxOutputTokens}
                onChange={(e) => onChange({ ...editing, maxOutputTokens: Number(e.target.value) || 0 })}
              />
            </Field>
            <Field label="prompt 单价（credits / 1k tokens；空=不支持按 token 计费）">
              <input
                type="number"
                step="0.01"
                className="input"
                value={editing.promptPriceCredits}
                onChange={(e) =>
                  onChange({
                    ...editing,
                    promptPriceCredits: e.target.value === '' ? '' : Number(e.target.value),
                  })
                }
              />
            </Field>
            <Field label="completion 单价（credits / 1k tokens）">
              <input
                type="number"
                step="0.01"
                className="input"
                value={editing.completionPriceCredits}
                onChange={(e) =>
                  onChange({
                    ...editing,
                    completionPriceCredits: e.target.value === '' ? '' : Number(e.target.value),
                  })
                }
              />
            </Field>
          </div>
          <Field label="备注">
            <input className="input" value={editing.remark} onChange={(e) => onChange({ ...editing, remark: e.target.value })} />
          </Field>
          <Check label="启用" checked={editing.isActive} onChange={(v) => onChange({ ...editing, isActive: v })} />
        </div>
        <div className="px-6 py-4 border-t border-light-border flex items-center justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm rounded-lg border border-light-border hover:bg-light-hover">取消</button>
          <button onClick={onSubmit} disabled={submitting} className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-accent-blue text-white disabled:opacity-50">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />} 保存
          </button>
        </div>
      </div>
    </div>
  );
}

interface TestModalProps {
  modelId: string;
  modelName: string;
  onClose: () => void;
}

function TestCallModal({ modelId, modelName, onClose }: TestModalProps) {
  const [systemPrompt, setSystemPrompt] = useState('');
  const [message, setMessage] = useState('你好，做一次自检');
  const [stream, setStream] = useState(false);
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState('');
  const [reasoning, setReasoning] = useState('');
  const [errorText, setErrorText] = useState('');
  const [usage, setUsage] = useState<{ promptTokens: number; completionTokens: number; totalTokens: number } | null>(null);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);
  const [abortCtrl, setAbortCtrl] = useState<AbortController | null>(null);

  const onRun = async () => {
    setOutput('');
    setReasoning('');
    setErrorText('');
    setUsage(null);
    setLatencyMs(null);
    setRunning(true);
    const ctrl = new AbortController();
    setAbortCtrl(ctrl);
    const payload = {
      message,
      systemPrompt: systemPrompt.trim() || undefined,
    };
    try {
      if (stream) {
        await llmPlatformAPI.streamTestModel(
          modelId,
          payload,
          (evt) => {
            if (evt._error) {
              setErrorText(`[${evt._error.code ?? 'error'}] ${evt._error.message}`);
              return;
            }
            if (evt._final) {
              if (evt._final.usage) setUsage(evt._final.usage);
              if (typeof evt.latencyMs === 'number') setLatencyMs(evt.latencyMs);
              return;
            }
            if (evt.contentDelta) setOutput((s) => s + evt.contentDelta);
            if (evt.reasoningDelta) setReasoning((s) => s + evt.reasoningDelta);
            if (evt.usage) setUsage(evt.usage);
          },
          ctrl.signal
        );
      } else {
        const res = await llmPlatformAPI.testModel(modelId, payload);
        setOutput(res.data.content);
        if (res.data.reasoningContent) setReasoning(res.data.reasoningContent);
        setUsage(res.data.usage);
        setLatencyMs(res.latencyMs);
      }
    } catch (err) {
      setErrorText(err instanceof Error ? err.message : '调用失败');
    } finally {
      setRunning(false);
      setAbortCtrl(null);
    }
  };

  const onAbort = () => abortCtrl?.abort();

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-light-border flex items-center justify-between">
          <h3 className="text-lg font-semibold">测试模型：<span className="font-mono text-sm">{modelName}</span></h3>
          <button onClick={onClose} className="text-light-muted hover:text-light-text">×</button>
        </div>
        <div className="p-6 space-y-3">
          <Field label="System Prompt（可选）">
            <textarea
              rows={3}
              className="input"
              placeholder="留空表示不传 system 消息"
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
            />
          </Field>
          <Field label="测试消息（user）" required>
            <textarea
              rows={3}
              className="input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </Field>
          <div className="flex items-center gap-3">
            <Check label="走流式" checked={stream} onChange={setStream} />
            {!running ? (
              <button
                onClick={() => void onRun()}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-accent-blue text-white"
              >
                <Play className="w-4 h-4" /> 发起调用
              </button>
            ) : (
              <button
                onClick={onAbort}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-red-500 text-white"
              >
                取消
              </button>
            )}
          </div>

          {errorText && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded">
              {errorText}
            </div>
          )}

          {(usage || latencyMs != null) && (
            <div className="text-xs text-light-muted">
              {usage && (
                <span className="mr-3">tokens: prompt {usage.promptTokens} / completion {usage.completionTokens} / total {usage.totalTokens}</span>
              )}
              {latencyMs != null && <span>耗时 {latencyMs}ms</span>}
            </div>
          )}

          {reasoning && (
            <div>
              <label className="block text-sm text-light-muted mb-1">推理过程</label>
              <pre className="bg-gray-50 border border-light-border rounded p-3 text-xs whitespace-pre-wrap max-h-40 overflow-y-auto">{reasoning}</pre>
            </div>
          )}

          <div>
            <label className="block text-sm text-light-muted mb-1">输出</label>
            <pre className="bg-gray-50 border border-light-border rounded p-3 text-xs whitespace-pre-wrap min-h-[120px] max-h-[40vh] overflow-y-auto">{output || '（无输出）'}</pre>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-light-border flex items-center justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-light-border hover:bg-light-hover">关闭</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div>
      <label className="block text-sm text-light-muted mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}
